import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, Building2, Users, Cpu, Activity, Database,
    AlertTriangle, ClipboardList, Clock, Globe, Save, KeyRound, Ban,
    Copy, Check, Mail, X,
} from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import {
    SACard, MetricCard, StatusPill, formatBytes, timeAgo,
} from "@/components/superadmin/SAPrimitives";
import SATenantActions from "@/components/superadmin/SATenantActions";

export default function SuperAdminTenantDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState("overview");

    const load = useCallback(async () => {
        try {
            const { data } = await http.get(`/superadmin/tenants/${id}`);
            setData(data);
            setErr("");
        } catch (e) {
            setErr(errMsg(e));
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const doAction = async (action, payload = {}) => {
        try {
            if (action === "delete") {
                await http.delete(`/superadmin/tenants/${id}`, { data: payload });
                toast.success("Scheduled for purge in 30 days");
                // redirect back to list — this tenant is now hidden by default
                navigate("/superadmin/tenants", { replace: true });
                return;
            }
            if (action === "suspend") await http.post(`/superadmin/tenants/${id}/suspend`, payload);
            if (action === "activate") await http.post(`/superadmin/tenants/${id}/activate`, payload);
            if (action === "restore") await http.post(`/superadmin/tenants/${id}/restore`, payload);
            toast.success("Updated");
            load();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    const revokeDevice = async (activationId) => {
        try {
            await http.post(`/superadmin/tenants/${id}/licensing/devices/${activationId}/revoke`);
            toast.success("Device activation revoked");
            load();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    if (err) return <SACard className="text-red-300" data-testid="sa-tenant-detail-error">{err}</SACard>;
    if (!data) return <SACard>Loading…</SACard>;

    const { tenant, users, agents, jobs, audit, usage, licensing, license_devices: licenseDevices } = data;
    const online = agents.filter((a) => a.status !== "offline").length;

    return (
        <div className="space-y-5" data-testid="sa-tenant-detail">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Link
                        to="/superadmin/tenants"
                        className="rounded-md border border-white/10 p-2 text-white/65 hover:bg-white/5"
                    >
                        <ArrowLeft size={14} />
                    </Link>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-[22px] font-semibold tracking-tight">
                                {tenant.name}
                            </h1>
                            <StatusPill status={tenant.deleted_at ? (tenant.status === "purged" ? "purged" : "deleted") : tenant.status || "active"} />
                        </div>
                        <div className="mt-0.5 text-[11px] text-white/55">
                            {tenant.slug} · plan {tenant.plan || "free"} · created {timeAgo(tenant.created_at)}
                        </div>
                    </div>
                </div>
                <SATenantActions
                    tenant={tenant}
                    onAction={(action, payload) => doAction(action, payload)}
                />
            </div>

            {tenant.deleted_at && (
                <SACard className="border-red-400/30 bg-red-400/5 text-[12px] text-red-200">
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={14} /> <strong>Pending hard-purge.</strong>
                    </div>
                    <div className="mt-1 text-white/65">
                        Scheduled to be deleted permanently at {tenant.purge_at || "—"}. Use
                        the <strong>Restore</strong> action to cancel.
                    </div>
                </SACard>
            )}

            <RegionPanel tenant={tenant} onSaved={load} />

            {/* Usage metrics */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="sa-tenant-usage">
                <MetricCard icon={Database} label="Data (30d)" value={formatBytes(usage.bytes_transferred_30d)} sublabel={`${usage.completed_jobs_30d} completed`} />
                <MetricCard icon={Activity} label="Active Jobs" value={usage.active_jobs} sublabel="running + queued" />
                <MetricCard icon={AlertTriangle} label="Failed Jobs" value={usage.failed_jobs_30d} sublabel={`${usage.error_rate_pct || 0}% error rate`} tone={(usage.error_rate_pct || 0) > 20 ? "bad" : (usage.error_rate_pct || 0) > 5 ? "warn" : "good"} />
                <MetricCard icon={Cpu} label="Agents" value={`${online}/${agents.length}`} sublabel={`${users.length} users`} />
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-white/5">
                {["overview", "licensing", "users", "agents", "jobs", "audit"].map((t) => (
                    <button
                        key={t}
                        data-testid={`sa-tenant-tab-${t}`}
                        onClick={() => setTab(t)}
                        className={[
                            "relative px-3 py-2 text-[12px] font-medium transition-colors",
                            tab === t ? "text-white" : "text-white/55 hover:text-white/85",
                        ].join(" ")}
                    >
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                        {tab === t && (
                            <span
                                className="absolute -bottom-px left-3 right-3 h-[2px] rounded-full"
                                style={{ background: "linear-gradient(90deg, #004AAD, #00C2FF)" }}
                            />
                        )}
                    </button>
                ))}
            </div>

            {tab === "overview" && <Overview tenant={tenant} users={users} agents={agents} jobs={jobs} />}
            {tab === "licensing" && (
                <LicensingPanel licensing={licensing} devices={licenseDevices} onRevoke={revokeDevice} />
            )}
            {tab === "users" && <UsersTable rows={users} />}
            {tab === "agents" && <AgentsTable rows={agents} />}
            {tab === "jobs" && <JobsTable rows={jobs} />}
            {tab === "audit" && <AuditTable rows={audit} />}
        </div>
    );
}

function Overview({ tenant, users, agents, jobs }) {
    return (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <SACard>
                <SectionHeading icon={Building2} title="Workspace" />
                <Row label="Name" value={tenant.name} />
                <Row label="Slug" value={tenant.slug} mono />
                <Row label="Tenant ID" value={tenant.id} mono small />
                <Row label="Plan" value={(tenant.plan || "free").toUpperCase()} />
                <Row label="Status" value={<StatusPill status={tenant.deleted_at ? (tenant.status === "purged" ? "purged" : "deleted") : tenant.status || "active"} />} />
                <Row label="Created" value={timeAgo(tenant.created_at)} />
                {tenant.suspended_at && (
                    <>
                        <Row label="Suspended" value={timeAgo(tenant.suspended_at)} />
                        {tenant.suspend_reason && <Row label="Reason" value={tenant.suspend_reason} />}
                    </>
                )}
                {tenant.deleted_at && (
                    <>
                        <Row label="Deleted" value={timeAgo(tenant.deleted_at)} />
                        <Row label="Purge at" value={tenant.purge_at || "—"} small />
                    </>
                )}
            </SACard>

            <SACard>
                <SectionHeading icon={Users} title={`Users · ${users.length}`} />
                <ul className="space-y-2 text-[12px]">
                    {users.slice(0, 6).map((u) => (
                        <li key={u.id} className="flex items-center justify-between">
                            <div className="min-w-0">
                                <div className="truncate">{u.name || u.email}</div>
                                <div className="truncate text-[10.5px] text-white/45">{u.email}</div>
                            </div>
                            <span className="shrink-0 rounded-sm bg-white/5 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-white/65">
                                {u.role}
                            </span>
                        </li>
                    ))}
                    {users.length > 6 && (
                        <li className="text-[11px] text-white/45">+ {users.length - 6} more</li>
                    )}
                </ul>
            </SACard>

            <SACard>
                <SectionHeading icon={Activity} title={`Recent Jobs · ${jobs.length}`} />
                <ul className="space-y-2 text-[12px]">
                    {jobs.slice(0, 6).map((j) => (
                        <li key={j.id} className="flex items-center justify-between">
                            <div className="min-w-0">
                                <div className="truncate">{j.name || "(unnamed)"}</div>
                                <div className="text-[10.5px] text-white/45">{timeAgo(j.created_at)}</div>
                            </div>
                            <StatusPill status={j.status || "?"} />
                        </li>
                    ))}
                    {jobs.length === 0 && <li className="text-[12px] text-white/40">No jobs yet.</li>}
                </ul>
            </SACard>
        </div>
    );
}

function LicensingPanel({ licensing, devices, onRevoke }) {
    const [confirming, setConfirming] = useState(null);

    if (!licensing) return <SACard>No licensing data.</SACard>;
    const seats = licensing.seats || {};

    return (
        <div className="space-y-3" data-testid="sa-tenant-licensing">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <MetricCard icon={KeyRound} label="Plan" value={licensing.plan_label} sublabel={licensing.status} />
                <MetricCard icon={Cpu} label="Devices" value={`${seats.used ?? 0}/${seats.max_devices ?? "∞"}`} sublabel={`${seats.available ?? "—"} available`} />
                <MetricCard icon={Activity} label="Concurrency cap" value={licensing.concurrency_cap ?? "Unlimited"} sublabel="simultaneous migrations" />
                <MetricCard icon={ClipboardList} label="Reference" value={licensing.reference_id} sublabel="license ID" />
            </div>

            <SACard className="p-0" data-testid="sa-tenant-license-devices">
                <table className="w-full text-[12px]">
                    <TableHead cols={["Device", "Kind", "Activated", "Last Seen", "Status", "Actions"]} />
                    <tbody>
                        {(devices || []).map((d) => (
                            <tr key={d.id} className="border-b border-white/5">
                                <td className="px-5 py-2.5">{d.device_name || "—"}</td>
                                <td className="px-2 py-2.5 uppercase text-white/70">{d.kind || "—"}</td>
                                <td className="px-2 py-2.5 text-white/55">{timeAgo(d.activated_at)}</td>
                                <td className="px-2 py-2.5 text-white/55">{timeAgo(d.last_seen_at)}</td>
                                <td className="px-2 py-2.5">
                                    <StatusPill status={d.status === "active" ? "active" : "suspended"} />
                                </td>
                                <td className="px-5 py-2.5">
                                    {d.status === "active" && (
                                        confirming === d.id ? (
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="text-white/55">Revoke?</span>
                                                <button
                                                    data-testid={`sa-revoke-confirm-${d.id}`}
                                                    onClick={() => { onRevoke(d.id); setConfirming(null); }}
                                                    className="rounded bg-red-500/80 px-2 py-1 text-[11px] font-medium text-white hover:bg-red-500"
                                                >
                                                    Confirm
                                                </button>
                                                <button
                                                    onClick={() => setConfirming(null)}
                                                    className="rounded border border-white/10 px-2 py-1 text-[11px] text-white/65 hover:bg-white/5"
                                                >
                                                    Cancel
                                                </button>
                                            </span>
                                        ) : (
                                            <button
                                                data-testid={`sa-revoke-btn-${d.id}`}
                                                onClick={() => setConfirming(d.id)}
                                                className="inline-flex items-center gap-1.5 rounded border border-white/10 px-2 py-1 text-[11px] text-white/70 hover:border-red-400/50 hover:text-red-300"
                                            >
                                                <Ban size={11} /> Revoke
                                            </button>
                                        )
                                    )}
                                </td>
                            </tr>
                        ))}
                        {(!devices || devices.length === 0) && (
                            <tr><td colSpan={6} className="px-5 py-8 text-center text-white/40">No devices activated.</td></tr>
                        )}
                    </tbody>
                </table>
            </SACard>
        </div>
    );
}

function SectionHeading({ icon: Icon, title }) {
    return (
        <div className="mb-3 flex items-center gap-2 border-b border-white/5 pb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
            <Icon size={12} /> {title}
        </div>
    );
}

function Row({ label, value, mono, small }) {
    return (
        <div className="flex items-start justify-between gap-3 border-b border-white/5 py-1.5 last:border-none">
            <span className="text-[11px] uppercase tracking-wider text-white/45">{label}</span>
            <span
                className={[
                    "text-right text-white/90",
                    mono ? "font-mono" : "",
                    small ? "text-[10.5px]" : "text-[12px]",
                    "break-all",
                ].join(" ")}
            >
                {value || "—"}
            </span>
        </div>
    );
}

function UsersTable({ rows }) {
    const [resetLink, setResetLink] = useState(null); // { email, link, expires_in_minutes, email_delivery }
    const [busyId, setBusyId] = useState(null);

    const resetPassword = async (u) => {
        setBusyId(u.id);
        try {
            const { data } = await http.post(`/superadmin/users/${u.id}/reset-password`);
            setResetLink({ email: u.email, ...data });
            toast.success(
                data.email_delivery === "sent" ? "Reset email sent." : "Reset link generated — copy it below.",
            );
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <SACard className="p-0" data-testid="sa-tenant-users">
            <table className="w-full text-[12px]">
                <TableHead cols={["Name", "Email", "Role", "Created", "Actions"]} />
                <tbody>
                    {rows.map((u) => (
                        <tr key={u.id} className="border-b border-white/5">
                            <td className="px-5 py-2.5">{u.name || "—"}</td>
                            <td className="px-2 py-2.5 text-white/80">{u.email}</td>
                            <td className="px-2 py-2.5 uppercase text-white/70">{u.role}</td>
                            <td className="px-2 py-2.5 text-white/55">{timeAgo(u.created_at)}</td>
                            <td className="px-5 py-2.5 text-right">
                                <button
                                    onClick={() => resetPassword(u)}
                                    disabled={busyId === u.id}
                                    data-testid={`sa-reset-password-btn-${u.id}`}
                                    className="inline-flex items-center gap-1.5 rounded border border-white/10 px-2 py-1 text-[11px] text-white/70 hover:border-blue-400/50 hover:text-blue-300 disabled:opacity-50"
                                >
                                    <KeyRound size={11} /> {busyId === u.id ? "Sending…" : "Reset password"}
                                </button>
                            </td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-white/40">No users.</td></tr>}
                </tbody>
            </table>
            {resetLink && <ResetLinkModal data={resetLink} onClose={() => setResetLink(null)} />}
        </SACard>
    );
}

function ResetLinkModal({ data, onClose }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(data.reset_link);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" data-testid="sa-reset-link-modal">
            <div className="w-full max-w-lg rounded-lg border border-white/10 bg-[#0a142b] p-5 text-white shadow-xl">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[13px] font-semibold">
                        <KeyRound size={14} className="text-blue-300" /> Password reset for {data.email}
                    </div>
                    <button onClick={onClose} className="text-white/50 hover:text-white"><X size={16} /></button>
                </div>
                <div className="mt-3 flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-[11.5px] text-white/70">
                    {data.email_delivery === "sent" ? (
                        <><Mail size={13} className="shrink-0 text-emerald-300" /> Emailed to the user directly.</>
                    ) : (
                        <><AlertTriangle size={13} className="shrink-0 text-amber-300" /> Email delivery isn't configured on this environment — copy the link below and send it to the user yourself.</>
                    )}
                </div>
                <div className="mt-3">
                    <div className="mb-1 text-[10.5px] uppercase tracking-wider text-white/45">
                        Reset link — expires in {data.expires_in_minutes} minutes, single use
                    </div>
                    <div className="flex items-center gap-2">
                        <code className="flex-1 truncate rounded border border-white/10 bg-black/30 px-2 py-1.5 font-mono text-[11px] text-white/80">
                            {data.reset_link}
                        </code>
                        <button
                            onClick={copy}
                            data-testid="sa-reset-link-copy"
                            className="shrink-0 rounded border border-white/10 p-1.5 text-white/65 hover:bg-white/5"
                        >
                            {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                        </button>
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={onClose}
                        data-testid="sa-reset-link-close"
                        className="rounded-md border border-white/10 px-3 py-1.5 text-[12px] text-white/75 hover:bg-white/5"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}

function AgentsTable({ rows }) {
    return (
        <SACard className="p-0" data-testid="sa-tenant-agents">
            <table className="w-full text-[12px]">
                <TableHead cols={["Name", "Kind", "Role", "OS", "Status", "Last Heartbeat"]} />
                <tbody>
                    {rows.map((a) => (
                        <tr key={a.id} className="border-b border-white/5">
                            <td className="px-5 py-2.5">{a.name || "—"}</td>
                            <td className="px-2 py-2.5 uppercase text-white/70">{a.kind || "—"}</td>
                            <td className="px-2 py-2.5 uppercase text-white/70">{a.role || "—"}</td>
                            <td className="px-2 py-2.5 text-white/65">{a.os || "—"}</td>
                            <td className="px-2 py-2.5">
                                <StatusPill status={a.status === "offline" ? "suspended" : "active"} />
                            </td>
                            <td className="px-5 py-2.5 text-white/55">{timeAgo(a.last_heartbeat)}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-white/40">No agents.</td></tr>}
                </tbody>
            </table>
        </SACard>
    );
}

function JobsTable({ rows }) {
    return (
        <SACard className="p-0" data-testid="sa-tenant-jobs">
            <table className="w-full text-[12px]">
                <TableHead cols={["Name", "Status", "Size", "Started", "Duration"]} />
                <tbody>
                    {rows.map((j) => (
                        <tr key={j.id} className="border-b border-white/5">
                            <td className="px-5 py-2.5">{j.name || "(unnamed)"}</td>
                            <td className="px-2 py-2.5"><StatusPill status={j.status || "?"} /></td>
                            <td className="px-2 py-2.5 text-white/80">
                                {formatBytes(j.bytes_transferred || 0)}
                            </td>
                            <td className="px-2 py-2.5 text-white/55">{timeAgo(j.created_at)}</td>
                            <td className="px-5 py-2.5 text-white/55">{j.duration || "—"}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-white/40">No jobs.</td></tr>}
                </tbody>
            </table>
        </SACard>
    );
}

function AuditTable({ rows }) {
    return (
        <SACard className="p-0" data-testid="sa-tenant-audit">
            <table className="w-full text-[12px]">
                <TableHead cols={["Action", "Actor", "Detail", "When"]} />
                <tbody>
                    {rows.map((a, i) => (
                        <tr key={a.id || `${a.action}-${a.created_at || i}`} className="border-b border-white/5">
                            <td className="px-5 py-2.5 font-mono text-[11px] text-white/85">{a.action}</td>
                            <td className="px-2 py-2.5 text-white/80">{a.actor_email || a.actor_id || "system"}</td>
                            <td className="px-2 py-2.5 text-white/55">{a.detail ? JSON.stringify(a.detail).slice(0, 90) : "—"}</td>
                            <td className="px-5 py-2.5 text-white/55">{timeAgo(a.created_at)}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-white/40">No audit trail.</td></tr>}
                </tbody>
            </table>
        </SACard>
    );
}

function TableHead({ cols }) {
    return (
        <thead>
            <tr className="border-b border-white/5 text-left text-[10px] uppercase tracking-wider text-white/45">
                {cols.map((c, i) => (
                    <th
                        key={`th-${c}-${i}`}
                        className={[
                            "py-2.5 font-medium",
                            i === 0 ? "px-5" : i === cols.length - 1 ? "px-5" : "px-2",
                        ].join(" ")}
                    >
                        {c}
                    </th>
                ))}
            </tr>
        </thead>
    );
}


const REGION_OPTIONS = [
    { v: "global", label: "Global", desc: "No regional pinning — alerts/CDN use platform default." },
    { v: "us",     label: "US",     desc: "us-east / us-west routing for alerts and agent CDN." },
    { v: "eu",     label: "EU",     desc: "eu-central routing — GDPR-friendly default for European tenants." },
    { v: "apac",   label: "APAC",   desc: "ap-southeast routing for Asia-Pacific tenants." },
];

function RegionPanel({ tenant, onSaved }) {
    const [region, setRegion] = useState(tenant.region || "global");
    const [saving, setSaving] = useState(false);
    const dirty = region !== (tenant.region || "global");

    const save = async () => {
        setSaving(true);
        try {
            await http.patch(`/superadmin/tenants/${tenant.id}/region`, { region });
            toast.success(`Region pinned to ${region.toUpperCase()}`);
            onSaved?.();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <SACard data-testid="sa-region-panel">
            <div className="mb-3 flex items-center gap-2 text-[12px] font-semibold text-white/80">
                <Globe size={14} /> Data residency region
                <span className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-white/55">
                    Logical pinning
                </span>
            </div>
            <p className="mb-3 text-[11px] text-white/55">
                Region label gates alert routing (Slack/Teams/email payloads carry it as a field) and
                the agent download manifest (per-region CDN override via <code>MOOVBYTE_CDN_BASE_&lt;REGION&gt;</code> env).
                Hard isolation (per-region MongoDB) is a future P3 feature.
            </p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
                {REGION_OPTIONS.map((o) => (
                    <button
                        key={o.v}
                        onClick={() => setRegion(o.v)}
                        data-testid={`sa-region-option-${o.v}`}
                        className={[
                            "rounded border p-3 text-left transition-colors",
                            region === o.v
                                ? "border-blue-400/60 bg-blue-500/10 text-blue-100"
                                : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/30",
                        ].join(" ")}
                    >
                        <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider">
                            {o.label}
                            {(tenant.region || "global") === o.v && (
                                <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 font-mono text-[9px] text-emerald-200">
                                    current
                                </span>
                            )}
                        </div>
                        <div className="mt-1 text-[11px] text-white/55">{o.desc}</div>
                    </button>
                ))}
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
                <button
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid="sa-region-save-btn"
                    className="inline-flex items-center gap-2 rounded bg-blue-500 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-blue-600 disabled:opacity-50"
                >
                    <Save size={12} /> {saving ? "Saving…" : "Save region"}
                </button>
            </div>
        </SACard>
    );
}
